/**
 * The icon library: a searchable, grouped grid of Lucide icons that can be
 * dragged onto the canvas (or clicked to apply to the selected icon node).
 *
 * Tiles render the icon live at the panel's own stroke/size, not a bitmap, so
 * what you see is what lands on the canvas.
 */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LucideAngularModule } from 'lucide-angular';

import { DESIGN_ICON_DRAG_TYPE } from '../../design-builder.types';
import {
  type IconCatalogueEntry,
  type IconCatalogueGroup,
  searchIcons,
} from './design-icon-catalogue';

@Component({
  selector: 'app-design-icon-library',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, LucideAngularModule],
  templateUrl: './icon-library.component.html',
  styleUrl: './icon-library.component.scss',
})
export class IconLibraryComponent {
  /** Name of the icon on the selected node, so it can be marked as current. */
  readonly activeIconName = input<string | null>(null);

  /** Clicked an icon — the shell applies it to the selection, or inserts it. */
  readonly iconPicked = output<IconCatalogueEntry>();

  readonly search = signal('');

  readonly groups = computed<IconCatalogueGroup[]>(() => searchIcons(this.search()));

  readonly isEmpty = computed(() => !this.groups().length);

  readonly total = computed(() =>
    this.groups().reduce((sum, group) => sum + group.entries.length, 0),
  );

  onDragStart(entry: IconCatalogueEntry, event: DragEvent): void {
    // Only the NAME travels; the canvas resolves it against the same catalogue.
    event.dataTransfer?.setData(DESIGN_ICON_DRAG_TYPE, entry.name);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'copy';
  }

  onPick(entry: IconCatalogueEntry): void {
    this.iconPicked.emit(entry);
  }

  isActive(entry: IconCatalogueEntry): boolean {
    return this.activeIconName() === entry.name;
  }

  trackByName(_: number, entry: IconCatalogueEntry): string {
    return entry.name;
  }

  trackByGroup(_: number, group: IconCatalogueGroup): string {
    return group.key;
  }
}
